import React, { useContext } from 'react'

import DeepLinkButtons from '../Common/DeepLinkButtons'
import ItineraryItemCard from '../ItineraryItemCard'

import InlineGroup from 'components/Common/InlineGroup'
import GeoContext from 'contexts/geoContext'
import useCheapestOfTourV2Offer from 'hooks/TourV2/useCheapestOfTourV2Offer'
import useTourV2VariationDetails from 'hooks/TourV2/useTourV2VariationDetails'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import {
  TourV2OfferCardPriceDetails,
  getItineraryDaysCount,
} from 'tripPlanner/components/Bookmark/BookmarkCard/TourV2Card'
import BookmarkLocation from 'tripPlanner/components/Bookmark/BookmarkCardNew/BookmarkLocation'
import SaveTripItemButton from 'tripPlanner/components/Buttons/SaveTripItemButton'
import SavedOfferCard from 'tripPlanner/components/SavedOffers/SavedOfferCard'
import TripContext from 'tripPlanner/contexts/TripContext'
import useTourV2ItemConfirmationCodes from 'tripPlanner/hooks/confirmationCodes/useTourV2ItemConfirmationCodes'
import useTourV2ItemOccupancyDescription from 'tripPlanner/hooks/occupancy/useTourV2ItemOccupancyDescription'
import { TourItem } from 'tripPlanner/types/tripItem'
import {
  formatDatesAndDays,
  formatDurationDaysIfMultipleDays,
  formatStartAndEndLocation,
  isLeBookmark,
} from 'tripPlanner/utils'
import { buildProductLink } from 'tripPlanner/utils/bookmark/deepLink'

interface Props {
  item: TourItem
  offer: Tours.V2Offer
}

export default function TourV2ItemCard({ offer, item }: Props) {
  const {
    cheapestPurchasableOption: purchasableOption,
    cheapestVariation: variation,
  } = useCheapestOfTourV2Offer(offer)
  const variationDetails = useTourV2VariationDetails(variation)

  const viewAsTemplate = useContext(TripContext)?.viewAsTemplate

  const { currentCurrency } = useContext(GeoContext)

  const timeFormat = useRegionTimeFormat()

  const bookingId = useTourV2ItemConfirmationCodes(item)
  const travellersDescription = useTourV2ItemOccupancyDescription(item)

  if (!purchasableOption || !variation || !variationDetails) {
    return null
  }
  const { startLocation, endLocation, hasTheSameStartAndEndLocation } =
    variationDetails

  const description = `Operated by: ${offer.brand.name}`
  const locationDescription = (
    <BookmarkLocation
      startLocation={startLocation}
      endLocation={hasTheSameStartAndEndLocation ? startLocation : endLocation}
      variant="start_and_end"
      showIcon={false}
    />
  )
  const image = variation.images[0]

  const timeDescription = formatDatesAndDays(item, timeFormat)
  const durationDescription = formatDurationDaysIfMultipleDays(item)

  if (isLeBookmark(item)) {
    const tileLink = buildProductLink(item, !viewAsTemplate, { offer }) ?? ''

    const priceAreaContent = (
      <TourV2OfferCardPriceDetails
        depositComponent={null}
        purchasableOption={purchasableOption}
        duration={getItineraryDaysCount(variation)}
        currencyCode={currentCurrency}
      />
    )
    const ctaAreaContent = isLeBookmark(item) ? (
      <InlineGroup vertical="center" horizontal="between" spacing="md">
        <SaveTripItemButton item={item} />
        <DeepLinkButtons className="cta-area" item={item} />
      </InlineGroup>
    ) : null

    return (
      <SavedOfferCard
        item={item}
        name={item.name}
        description={description}
        imgId={image?.id}
        imgSrc={image?.url}
        locationDescription={locationDescription}
        descriptors={[timeDescription, durationDescription]}
        printDescriptors={[
          timeDescription,
          durationDescription,
          travellersDescription,
        ]}
        priceAreaContent={priceAreaContent}
        ctaAreaContent={ctaAreaContent}
        to={tileLink}
        testId="saved-tour-card"
      />
    )
  } else {
    return (
      <ItineraryItemCard
        item={item}
        name={item.name}
        locationDescription={formatStartAndEndLocation(item)}
        descriptors={[timeDescription, durationDescription]}
        printDescriptors={[
          timeDescription,
          durationDescription,
          travellersDescription,
        ]}
        confirmationCode={bookingId.codeString}
        confirmationCodeLabel={bookingId.label}
      />
    )
  }
}
